/* eslint-disable better-mutation/no-mutation */

import { isServer } from 'tradera/utils/nextjs';

type Options = {
    timeout?: number;
    dataAttributes?: Record<string, string>;
    key?: string;
    unload?: () => void;
};

const scriptPromises = new Map<string, Promise<void>>();

export function loadScript(src: string, options: Options = {}) {
    if (isServer) {
        // Since we use a global variable we cannot support SSR as
        // all requests would share the same variable.
        throw new Error('loadScript() is not supported on the server');
    }
    const optionsWithDefaults = {
        timeout: options.timeout || 0,
        dataAttributes: options.dataAttributes || {}
    };
    const promise = scriptPromises.get(src);
    if (promise) {
        // Every time we call loadScript with the same src, we return the same promise.
        // This is so that everyone waiting for that promise can get the same result.
        // If we don't do this and loadScript is called twice, the second call can
        // resolve before script.onload() has happened.
        return promise;
    }

    const newPromise = new Promise<void>((resolve, reject) => {
        if (document.querySelector(`head > script[src="${src}"]`) !== null) {
            resolve();
            return;
        }

        if (options.key) {
            const scriptWithKey = document.querySelector(
                `head > script[data-key="${options.key}"]`
            );

            if (scriptWithKey) {
                document.head.removeChild(scriptWithKey);
                options.unload?.();
            }
        }

        const script = document.createElement('script');

        let timeoutTimer: ReturnType<typeof setTimeout>;
        if (optionsWithDefaults.timeout > 0) {
            timeoutTimer = setTimeout(() => {
                script.onload = null;
                script.onerror = null;
                document.head.removeChild(script);
                options.unload?.();
                reject(new Error('SCRIPT_LOAD_TIMEOUT'));
            }, optionsWithDefaults.timeout);
        }

        script.src = src;

        if (options.key) {
            optionsWithDefaults.dataAttributes = {
                ...optionsWithDefaults.dataAttributes,
                key: options.key
            };
        }
        for (const attribute of Object.keys(
            optionsWithDefaults.dataAttributes
        )) {
            script.setAttribute(
                `data-${attribute}`,
                optionsWithDefaults.dataAttributes[attribute]
            );
        }
        script.async = true;
        script.onload = () => {
            if (timeoutTimer) clearTimeout(timeoutTimer);
            resolve();
        };
        script.onerror = (error) => {
            if (timeoutTimer) clearTimeout(timeoutTimer);
            reject(error);
        };
        document.head.appendChild(script);
    });
    scriptPromises.set(src, newPromise);
    return newPromise;
}
